import React, { useCallback, useMemo, useState } from "react";

import { RouteComponentProps, useHistory } from "react-router";
import { useToggle } from "react-use";
import {
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Select,
  VStack,
  Flex,
  StatHelpText,
  HStack,
} from "@chakra-ui/react";

import useViewer from "../hooks/useViewer";

import useTranslations from "../hooks/useTranslations";

import { getReviewStatus } from "./ReviewListScreen";

import CampaignCreationModal from "../components/campaigns/CampaignCreationModal";

import FollowUpCard from "../components/reviews/FollowUpCard";
import { Tag } from "@chakra-ui/react";
import {
  ScreenHeader,
  ScreenContainer,
  Container,
  H3,
  Link,
  EmptyState,
  H6,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "../components/wombat-ui";

import { CampaignIcon, DotsVerticalIcon, PlusIcon } from "../components/icons";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import { NumberParam, useQueryParams } from "use-query-params";
import useNotification from "../hooks/useNotification";
import useConfirm from "../components/utils/confirm/useConfirm";
import { subMonths, subDays } from "date-fns";
import { TailSpinLoader } from "../components/loaders/TailSpinLoader";
import UserAvatarGroup from "../components/utils/UserAvatarGroup";
import { uniqBy } from "lodash";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import {
  ReviewFragment,
  useDeleteCampaign,
  useGetCampaign,
  useGetReportsMinimal,
  useGetReviews,
} from "../graphql/generated/definitions";

type TimePeriod =
  | "pastWeek"
  | "pastFifteenDays"
  | "pastMonth"
  | "pastThreeMonths"
  | "allTime";

const CampaignScreenContainer = ({ campaign }) => {
  const t = useTranslations();
  const { id: viewerId } = useViewer();

  useBreadcrumbs([
    {
      to: "/campaigns",
      text: t("campaign.campaigns") as string,
    },
    {
      to: `/campaigns/${campaign.id}`,
      text: campaign.name,
    },
  ]);

  const [{ page }, setQuery] = useQueryParams({
    page: NumberParam,
  });

  const availableTimePeriod: {
    [K in TimePeriod]: { createdAfter?: string };
  } = useMemo(
    () => ({
      pastWeek: {
        createdAfter: subDays(new Date(), 15).toISOString(),
      },
      pastFifteenDays: {
        createdAfter: subDays(new Date(), 15).toISOString(),
      },
      pastMonth: {
        createdAfter: subMonths(new Date(), 1).toISOString(),
      },
      pastThreeMonths: {
        createdAfter: subMonths(new Date(), 3).toISOString(),
      },
      allTime: {},
    }),
    []
  );

  const [selectedTimePeriod, setSelectedTimePeriod] =
    useState<TimePeriod>("pastWeek");

  const { data: reportsData, loading: reportsLoading } = useGetReportsMinimal({
    variables: {
      campaignId: campaign.id,
      ...availableTimePeriod[selectedTimePeriod],
    },
  });

  const { data: reviewData, loading: reviewLoading } = useGetReviews({
    variables: {
      page: page || 1,
      per: 10,
      campaignId: campaign.id,
      sortBy: "createdAt",
      direction: "DESC",
    },
  });

  const reviews = useMemo(
    () => reviewData?.reviews.collection || [],
    [reviewData, reviewLoading]
  );

  const unfilledReports = useMemo(() => {
    return reportsData?.reports.collection.filter(({ filledAt }) => !filledAt);
  }, [reportsData]);

  const unfilledReportsUsers = useMemo(
    () =>
      uniqBy(
        unfilledReports?.map(({ review }) => review.collaborator) || [],
        "id"
      ),
    [unfilledReports]
  );

  const openReports = useMemo(() => {
    return reportsData?.reports.collection
      .filter(
        ({ id }) =>
          !unfilledReports?.find(({ id: unfilledId }) => id === unfilledId)
      )
      .filter(({ checkedAt }) => !checkedAt);
  }, [reportsData, unfilledReports]);

  const openReportsUsers = useMemo(
    () =>
      uniqBy(
        openReports?.flatMap(({ reportReferents }) =>
          reportReferents
            .filter(({ filledAt }) => !filledAt)
            .map((referent) => referent?.referent)
        ),
        "id"
      ) || [],
    [openReports]
  );

  return (
    <Container>
      <div className="flex flex-col py-5">
        <VStack spacing="2">
          <Flex w="full" justifyContent="flex-end">
            <Select
              maxW="sm"
              defaultValue={Object.keys(availableTimePeriod)[0]}
              onChange={(e) => {
                setSelectedTimePeriod(e.target.value as TimePeriod);
              }}
            >
              {Object.keys(availableTimePeriod).map((key: TimePeriod) => (
                <option value={key} key={key}>
                  {t(`campaign.statistics.filter.${key}` as const)}
                </option>
              ))}
            </Select>
          </Flex>
          <StatGroup
            w="full"
            flexDir={["column", "row"]}
            experimental_spaceY={["3", "0"]}
            borderWidth="1px"
            borderRadius="md"
            p={6}
          >
            <Stat>
              <StatLabel>{t("campaign.reportsCount")}</StatLabel>
              {reportsLoading ? (
                <TailSpinLoader className="w-6 h-6" />
              ) : (
                <StatNumber>
                  {reportsData?.reports.metadata.totalCount}
                </StatNumber>
              )}
            </Stat>

            <Stat>
              <StatLabel>{t("campaign.openReportsCount")}</StatLabel>
              {reportsLoading ? (
                <TailSpinLoader className="w-6 h-6" />
              ) : (
                <>
                  {reportsData?.reports.metadata.totalCount > 0 ? (
                    <HStack>
                      <StatNumber>
                        {Math.round(
                          (openReports?.length /
                            reportsData?.reports.metadata.totalCount) *
                            100
                        )}
                        %
                      </StatNumber>

                      <UserAvatarGroup users={openReportsUsers} />
                    </HStack>
                  ) : (
                    <StatNumber>N/A</StatNumber>
                  )}
                  <StatHelpText>
                    {t("campaign.statistics.nReports", {
                      count: openReports?.length,
                    })}
                  </StatHelpText>
                </>
              )}
            </Stat>

            <Stat>
              <StatLabel>{t("campaign.unfilledReportsCount")}</StatLabel>
              {reportsLoading ? (
                <TailSpinLoader className="w-6 h-6" />
              ) : (
                <>
                  {reportsData?.reports.metadata.totalCount > 0 ? (
                    <HStack>
                      <StatNumber>
                        {Math.round(
                          (unfilledReports?.length /
                            reportsData?.reports.metadata.totalCount) *
                            100
                        )}
                        %
                      </StatNumber>
                      <UserAvatarGroup users={unfilledReportsUsers} />
                    </HStack>
                  ) : (
                    <StatNumber>N/A</StatNumber>
                  )}
                  <StatHelpText>
                    {t("campaign.statistics.nReports", {
                      count: unfilledReports?.length,
                    })}
                  </StatHelpText>
                </>
              )}
            </Stat>
          </StatGroup>
        </VStack>
        <H3>{t("campaign.followUps")}</H3>
        <List
          data={reviews}
          listOnly
          isLoading={reviewLoading}
          emptyMessage={t("campaign.noReviews") as string}
          id={`campaign-${campaign.id}-reviewList`}
          renderAsList={(review: ReviewFragment) => (
            <FollowUpCard
              key={review.id}
              review={review}
              reviewType={getReviewStatus(review)}
              isCollaboratorViewing={viewerId === review.collaborator.id}
            />
          )}
        />
        {!reviewLoading && (
          <Pagination
            page={page}
            onChange={(page) => setQuery({ page })}
            itemsCount={reviewData.reviews.metadata.totalCount}
            itemsPerPage={10}
          />
        )}
      </div>
    </Container>
  );
};

type CampaignScreenProps = RouteComponentProps<{ id: string }>;

const CampaignScreen: React.FC<CampaignScreenProps> = ({ match }) => {
  const t = useTranslations();
  const {
    isCapableOfReadingCampaigns,
    isCapableOfInsertingReviews,
    companyId,
  } = useViewer();

  // Match both id or slug
  const { data, loading, error } = useGetCampaign({
    variables: {
      id: match.params.id,
    },
  });

  const campaign = data?.campaign;

  const [campaignCreationModalOpen, toggleCampaignCreationModalOpen] =
    useToggle(false);

  const [deleteCampaign] = useDeleteCampaign();

  const confirm = useConfirm();
  const { notifySuccess } = useNotification();
  const history = useHistory();

  const handleDeleteCampaign = useCallback(async () => {
    confirm({
      title: t("campaign.delete"),
      message: t("campaign.deleteConfirm"),
      buttons: [
        { text: t("forms.actions.cancel") },
        {
          colorScheme: "red",
          text: t("forms.actions.delete"),
          id: "delete-campaign",
          onClick: async () => {
            await deleteCampaign({ variables: { input: { id: campaign.id } } });

            notifySuccess({
              title: t(`campaign.notification.success.delete.title`) as string,
              description: t(
                `campaign.notification.success.delete.body`
              ) as string,
            });

            history.push("/campaigns");
          },
        },
      ],
    });
  }, [campaign]);

  const actions = useMemo(
    () =>
      campaign && [
        isCapableOfInsertingReviews && (
          <Link
            variant="button-subtle"
            key="campaignCreateReview"
            to={`/reviews/new?campaignId=${campaign.id}`}
          >
            {t("header.createReview")}
            <PlusIcon className="w-4 h-4 ml-1 mt-0.5" />
          </Link>
        ),
        campaign.canInsert && (
          <Button
            onClick={toggleCampaignCreationModalOpen}
            variant="primary"
            key="editCampaignButton"
            data-test-id="editCampaignButton"
          >
            {t("campaign.edit")}
          </Button>
        ),
        campaign.canDestroy && (
          <Menu key="campaignContextMenu">
            <MenuButton
              id="deleteCampaignMenuTrigger"
              icon={DotsVerticalIcon}
              variant="ghost"
            />
            <MenuList>
              <MenuItem
                onSelect={handleDeleteCampaign}
                data-test-id="deleteCampaign"
              >
                {t("forms.actions.delete")}
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      ],
    [campaign, isCapableOfInsertingReviews]
  );

  return (
    <ScreenContainer
      loading={loading}
      error={
        !isCapableOfReadingCampaigns || error ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      {campaign && (
        <>
          <ScreenHeader
            title={
              <span className="flex items-center gap-2 space-x-4">
                {campaign?.name}
                <Tag>
                  {t(`campaign.reviewsCount`, {
                    reviewsCount: campaign.reviewsCount,
                  })}
                </Tag>
              </span>
            }
            actions={actions}
            description={
              campaign.description || t("campaign.missingDescription")
            }
            icon={CampaignIcon}
          />
          <CampaignScreenContainer campaign={campaign} />
          <CampaignCreationModal
            isOpen={campaignCreationModalOpen}
            onClose={toggleCampaignCreationModalOpen}
            defaultValues={{
              id: campaign.id,
              companyId,
              reminderEnabled: campaign.reminderEnabled ? "true" : "false",
              reminderGap: campaign.reminderGap,
              name: campaign.name as string,
              description: campaign.description as string,
            }}
          />
        </>
      )}
    </ScreenContainer>
  );
};

export default React.memo(CampaignScreen);
